/* @flow weak */
import camelCase from 'utils/camelCase';

/**
 * Shortcut to compute element style
 *
 * @param {HTMLElement} elem
 * @param {string} prop
 * @returns {string}
 */
export function getStyle(elem, prop) {
  if (typeof window.getComputedStyle !== 'undefined') {
    return window.getComputedStyle(elem, null).getPropertyValue(prop);
  } else {
    return elem.currentStyle[camelCase(prop)];
  }
}

/**
 * Get elements offset
 *
 * @param {HTMLElement} elem
 * @returns {{top: number, left: number}}
 */
export function getOffset(elem) {
  const docElem = document.documentElement;
  let box = { top: 0, left: 0 };

  // If we don't have gBCR, just use 0,0 rather than error
  // BlackBerry 5, iOS 3 (original iPhone)
  if (typeof elem.getBoundingClientRect !== 'undefined') {
    box = elem.getBoundingClientRect();
  }

  const scrollPosition = getScrollPosition();

  return {
    top: box.top + scrollPosition.top - docElem.clientTop,
    left: box.left + scrollPosition.left - docElem.clientLeft,
  };
}

/**
 * Get elements position
 *
 * @param {HTMLElement} elem
 * @param {HTMLElement?} offsetParent
 * @returns {{top: number, left: number}}
 */
export function getPosition(elem, offsetParent) {
  let offset;
  let parentOffset = { top: 0, left: 0 };

  // Fixed elements are offset from window (parentOffset = {top:0, left: 0}, because it is its only
  // offset parent
  if (getStyle(elem, 'position') === 'fixed') {
    // We assume that getBoundingClientRect is available when computed position is fixed
    offset = elem.getBoundingClientRect();
  } else {
    if (!offsetParent) {
      // Get *real* offsetParent
      offsetParent = getOffsetParent(elem); // eslint-disable-line no-param-reassign
    }

    // Get correct offsets
    offset = getOffset(elem);
    if (offsetParent.nodeName !== 'HTML') {
      parentOffset = getOffset(offsetParent);
    }

    // Add offsetParent borders
    parentOffset.top += parseInt(getStyle(offsetParent, 'border-top-width'), 10);
    parentOffset.left += parseInt(getStyle(offsetParent, 'border-left-width'), 10);
  }

  // Subtract parent offsets and element margins
  return {
    top: offset.top - parentOffset.top - parseInt(getStyle(elem, 'margin-top'), 10),
    left: offset.left - parentOffset.left - parseInt(getStyle(elem, 'margin-left'), 10),
  };
}

/**
 * Get parent element
 *
 * @param {HTMLElement?} elem
 * @returns {HTMLElement}
 */
export function getOffsetParent(elem) {
  const docElem = document.documentElement;
  let offsetParent = elem.offsetParent || docElem;

  while (offsetParent && (offsetParent.nodeName !== 'HTML' &&
    getStyle(offsetParent, 'position') === 'static')) {
    offsetParent = offsetParent.offsetParent;
  }

  return offsetParent || docElem;
}

/**
 * Get scroll position of an element (or document, if no element is provided)
 *
 * @param {HTMLElement?} elem
 * @returns {{top: number, left: number}}
 */
export function getScrollPosition(elem) {
  const docElem = document.documentElement;
  if (!elem || elem === document.body || elem === window) {
    elem = docElem; // eslint-disable-line no-param-reassign
  }
  return {
    left: elem === docElem ? (window.pageXOffset || docElem.scrollLeft) : elem.scrollLeft,
    top: elem === docElem ? (window.pageYOffset || docElem.scrollTop) : elem.scrollTop,
  };
}

export default {
  getStyle,
  getOffset,
  getPosition,
  getOffsetParent,
  getScrollPosition,
};



// WEBPACK FOOTER //
// ./src/lib/DOM.js